import { Component, OnInit, Input, OnDestroy, ViewChild, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from '@angular/router';
import { TitleCasePipe } from "@angular/common";
import { Observable, of, Subscription } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { ApplicationState, ApplicationType, InitiateWorkflowRequest, WorkflowData, WorkflowNote, ApplicationSteps, WorkflowFieldData, ApplicationStateChangeRequest } from '../../../../applications/models/applications.models';
import { ApplicationBaseComponent } from '../../../../applications/components/application-base.component';
import { LoaderService } from "../../../../common/services/loader.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../../applications/services/application-notes.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { AuthService } from "../../../../services/auth.service";
import { OrganizationStub } from '../../../../common/models/organization.model';
import { ComplianceReportingType } from '../../../models/compliance.model';
import { GISPointTypes, Well } from "../../../../common/models/well.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { WellSelectorComponent } from "../../well-selector/well-selector.component";
import { PaymentService, AuthorizationRequest } from "../../../../payments/services/payment.service";
import { PaymentItem, ApplicationPayment, Payment, ApplicationCreditAuthorization, AuthorizationResponse } from "../../../../payments/models/paymentItem.model";
import { ApplicationConfigService, ApplicationsConfig, ModuleConfig } from "../../../../services/application-config.service";
import { htmlEscape } from "../../../../Helpers/UtilityFunctions";
import { OnBaseDocumentTypeProcessReference } from '../../../../common/models/onBaseIntegrationAndMapping.model';


@Component({
    selector: 'as-drilled-plat-submit',
    templateUrl: './as-drilled-plat-submit.component.html',
    styleUrls: ['./as-drilled-plat-submit.component.scss'],
    standalone: false
})
export class AsDrilledPlatSubmitComponent extends ApplicationBaseComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() applicationState: ApplicationState;
    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];
    @Input() organization: OrganizationStub;

    @Input() reportType: ComplianceReportingType;

    @Output() onDeactivating: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(WellSelectorComponent) private _selector: WellSelectorComponent;

    steps = ApplicationSteps;

    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    saveUnsubmittedMessage: string = "Your report has been saved for completion at a later date.  For future reference, this report is stored as ID [XXX].";
    saveUnsubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return true; })]);
    canceledDialog: Dialog = new Dialog("Leave application unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.home(); }), new DialogButton("No", true, () => { }),]);
    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);


    private _config: ApplicationsConfig;

    get config(): ApplicationsConfig {
        return this._config;
    }

    get disableSubmit(): boolean {
        if (this.form == null) return false;
        return this.form.invalid;
    }

    get fees(): ApplicationPayment[] {
        if (this.form == null) return [];
        let group = this.form.get('payments');
        if (group == null) return [];
        let ctrl = group.get('payments');
        if (ctrl == null) return [];
        return ctrl.value;
    }


    set fees(value: ApplicationPayment[]) {
        if (this.form == null) return;
        let group = this.form.get('payments');
        if (group == null) return;
        let ctrl = group.get('payments');
        if (ctrl == null) return;
        ctrl.setValue(value);
    }

    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

    get isInCorrections(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Corrections;
    }

    get isApproved(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Approved;
    }

    get onBehalf(): boolean {
        return this.auth.User.isInternal;
    }

    get organizationId(): string {
        if (this.form == null) return "";
        let orgForm = this.form.get('organization');
        if (orgForm == null) return "";
        let idCtrl = orgForm.get('id');
        if (idCtrl == null) return "";
        return idCtrl.value;
    }

    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('selectedWell'); 
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get showLaterSteps(): boolean {
        return this.step != ApplicationSteps.SelectWell;
    }

    get step(): ApplicationSteps {
        if (this.form == null) return ApplicationSteps.SelectWell;
        let ctrl = this.form.get('step');
        if (ctrl == null) return ApplicationSteps.SelectWell;
        return <ApplicationSteps>ctrl.value;
    }

    get enableWellSelection(): boolean {
        if (this.application == null) return true;
        else {
            if (this.isApproved) return false;
            if (this.isInternal && this.isInCorrections) return false;
            if (this.getFilteredNotes('apiNumber').length != 0 && this.isInCorrections) return true;
            if (this.application.applicationState == ApplicationState.Input || this.application.applicationState == ApplicationState.Start) return true;
            return false;
        }
    }

    private _validPoints: GISPointTypes[] = [
        GISPointTypes.WellHead,
        GISPointTypes.Heel,
        GISPointTypes.Toe,
        GISPointTypes.Target
    ];

    constructor(loader: LoaderService, dialog: DynamicDialogService, subheader: SubHeaderService, auth: AuthService, appService: ApplicationsService, private configService: ApplicationConfigService,
        router: Router, sidePanelSvc: SidePanelService, noteSvc: ApplicationNotesService, private builder: FormBuilder, private casePipe: TitleCasePipe, private paymentService: PaymentService) {
        super(loader, dialog, subheader, auth, appService, router, sidePanelSvc, noteSvc);
        this.numericFields = ['unitization|platSize', 'wellInformation|points|wellHead|latitude', 'wellInformation|points|wellHead|longitude', 'wellInformation|points|target|latitude', 'wellInformation|points|target|longitude', 'wellInformation|points|toe|latitude', 'wellInformation|points|toe|longitude', 'wellInformation|points|heel|latitude', 'wellInformation|points|heel|longitude'];
        this.fileFields = ['documentation|finalAsDrilledPlat', 'documentation|preliminaryPlat', 'documentation|boreholeDeviationSurvey', 'documentation|revisedUnitPlat', 'documentation|royaltyInterestList'];
        this.trueBooleanFields = ['wellInformation|points|wellHead|queryRun', 'wellInformation|points|wellHead|coordinatesValidated', 'wellInformation|points|heel|queryRun', 'wellInformation|points|heel|coordinatesValidated', 'wellInformation|points|toe|queryRun', 'wellInformation|points|toe|coordinatesValidated', 'wellInformation|points|target|queryRun', 'wellInformation|points|target|coordinatesValidated', 'certification']
        this.paymentFields = ['payments|payments'];
        this.authorizationFields = ['payments|creditAuthorization'];
        this.dateFields = ['review|totalDepthDate', 'review|asDrilledPlatPreliminaryDate', 'review|asDrilledPlatFinalDate', 'review|boreholeDeviationSurveyDate', 'review|revisedUnitDate', 'review|asDrilledFeeDate', 'review|revisedUnitFeeDate'];
    }

    ngOnInit() {
        this.form = this.buildForm();
        if (this.application != null) {
            this.form = this.restoreFormData(this.form);
            if (this.form.get('payments').get('invoice').value != null) {
                this.form.get('payments').get('paymentType').disable();
                this.form.get('payments').get('option').disable();
            }
        }
        let obs$ = this.configService.getModuleConfig().pipe(
            tap((conf: ModuleConfig) => {
                if (conf != null && conf.applications != null) {
                    this._config = conf.applications;
                    if (this._config.permits != null && this._config.permits.documents != null) this.documentConfig = this._config.permits.documents;
                }
            })
        );
        let subs$ = obs$.subscribe(
            () => { },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    ngAfterViewInit() {
        if (this.application != null) {
            if (this._selector != null) {
                this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
                this._selector.findWells();
            }
            else {
                console.error("no selector component ready");
            }
        }       
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            wellName: [null, []],
            wellNumber: [null, []],
            organizationName: [this.organization.name, []],
            organization: [this.organization.id, []],
            rbdmsNumber: [this.organization.historicalOwnerNumber, []],
            reportType: [this.reportType.typeId, []],
            step: [ApplicationSteps.SelectWell, []],
            selectedWell: [null, [Validators.required]],
            apiNumber: [null, [Validators.required]],

            unitization: this.builder.group({
                subjectToOrder: [null, []],
                orderNumber: [{ value: null, disabled: true }, []],
                platSize: [null, []]
            }),
            documentation: this.builder.group({
                finalAsDrilledPlat: [[], []],
                preliminaryPlat: [[], []],
                boreholeDeviationSurvey: [[], []],
                revisedUnitPlat: [[], []],
                royaltyInterestList: [[], []]
            }),
            wellInformation: this.builder.group({
                apiNumber: [null, [Validators.required]],
                wellName: [null, []],
                wellNumber: [null, []],
                slant: [null, [Validators.required]],
                points: this.buildWellPointsForm()
            }),
            payments: this.builder.group({
                option: [null, [Validators.required]],
                paymentType: [null, []],
                payments: [[], []],
                creditAuthorization: [null, []],
                invoice: [null, []]
            }),
            review: this.builder.group({
                totalDepthDate: [null, []],
                sourceOfLocation: [[], []],
                asDrilledPlatPreliminaryDate: [null, []],
                asDrilledPlatFinalDate: [null, []],
                boreholeDeviationSurveyDate: [null, []],
                revisedUnitDate: [null, []],
                asDrilledFeeDate: [null, []],
                revisedUnitFeeDate: [null, []]
            })
        });
    }

    buildWellPointsForm(): FormGroup {
        let subForm: FormGroup = null
        let obj = {}
        this._validPoints.forEach(p => {
            let gp = this.builder.group({
                latitude: [null, [Validators.min(0)]],
                longitude: [null, [Validators.max(0)]],
                countyNumber: [{ value: null, disabled: true}, []],
                countyName: [{ value: null, disabled: true }, []],
                townshipName: [{ value: null, disabled: true }, []],
                townshipNumber: [{ value: null, disabled: true }, []],
                civilTownshipNumber: [{ value: null, disabled: true }, []],
                quadrangleName: [{ value: null, disabled: true }, []],
                quadrangleCode: [{ value: null, disabled: true }, []],
                section: [{ value: null, disabled: true }, []],
                lot: [{ value: null, disabled: true }, []],
                tract: [{ value: null, disabled: true }, []],
                queryRun: [false, []],
                coordinatesValidated: [true, [Validators.requiredTrue]]
            });            
            obj[p] = gp;
        });
        subForm = this.builder.group(obj);
        return subForm;
    }

    private initiateApplication(): Observable<WorkflowData> {
        let request: InitiateWorkflowRequest = {
            organizationId: this.form.get('organization').value,
            applicationType: ApplicationType.ComplianceReporting,
            onBehalf: this.onBehalf,
            reportType: this.reportType.typeId
        } as InitiateWorkflowRequest;
        return this.dataService.initiateApplication(request).pipe(
            tap((app: WorkflowData) => {
                this.application = app;
            })
        );
    }

    private getSaveObservable(): Observable<WorkflowData> {
        let init$: Observable<WorkflowData> = (this.application != null)
            ? of(this.application)
            : this.initiateApplication();

        let obs$ = init$.pipe(
            switchMap(() => {
                return this.saveProgress().pipe(tap((app) => { this.application = app; }));
            })
        );
        return obs$;
    }

    convertPaymentItemsToApplicationPayments(items: PaymentItem[]): ApplicationPayment[] {
        let appPmts: ApplicationPayment[] = [];
        items.forEach(p => {
            let pmt: ApplicationPayment = {
                id: p.id,
                description: p.description,
                fee: p.fee,
                department: p.department,
                fund: p.fund,
                ali: p.ali,
                program: p.program,
                revenueSourceCode: p.revenueSourceCode,
                reporting: p.reporting,
                accountDescription: p.accountDescription,
                applicationNumber: (this.application != null) ? this.application.applicationNumber : "",
                applicationDate: new Date((new Date).toUTCString()),
                applicationType: `Compliance Reporting - ${this.reportType.typeId}`,
                organization: this.organization.name,
                organizationId: this.organization.id
            } as ApplicationPayment;
            appPmts.push(pmt);
        });
        return appPmts;
    }

    onAuthorizationRequested(req: AuthorizationRequest): void {              
        req.flowUrl = htmlEscape(window.location.href);
        let payments: ApplicationPayment[] = <ApplicationPayment[]>this.form.get('payments').get('payments').value;
        if ((payments == null || payments.length == 0) && req.paymentInformation.paymentItems != null && req.paymentInformation.paymentItems.length > 0) {
            payments = this.convertPaymentItemsToApplicationPayments(req.paymentInformation.paymentItems);
        }
        req.paymentInformation.amount = 0;
        payments.forEach(p => { req.paymentInformation.amount += p.fee; });
        let appObs$: Observable<WorkflowData> = (this.application == null)
            ? this.initiateApplication()
            : of(this.application);
        appObs$ = appObs$.pipe(
            switchMap((app: WorkflowData) => {
                return this.saveProgress();
            })
        );
        let obs$: Observable<any> = appObs$.pipe(
            switchMap((saved: WorkflowData) => {
                let field = saved.workflowFieldDatas.find(f => this.paymentFields.includes(f.formControlName));
                payments.forEach(p => {
                    p.workflowFieldId = field.id;
                    p.workflowId = saved.id.toString();
                    p.applicationNumber = saved.applicationNumber;
                });
                field.applicationPayments = payments;
                return this.dataService.saveApplication(saved); //this is going to result in the application getting saved twice, but not sure how else to handle
            }),
            switchMap((saved: WorkflowData) => {
                this.application = saved;
                req.cancelUrl = `/issues/${this.application.applicationNumber}/resume`;
                req.failUrl = req.cancelUrl;
                req.successUrl = req.cancelUrl;
                this.allowDeactivate.next(true);
                console.log('save complete, sending to CBOSS');
                this.onDeactivating.emit(true);
                return this.paymentService.sendToCBoss(req);
            })
        );
        this._loader.toggleLoader(true);
        let subs$: Subscription = obs$.subscribe(
            (destination) => {
                this._loader.toggleLoader(false);
                if (destination != null) window.location.href = destination;
                else {
                    console.error("no destination received");
                    this.dialogService.showError();
                }
            }
        );
        this.subscription.add(subs$);
    }

    onFeesGenerated(pmts: PaymentItem[]): void {
        let appPmts = this.convertPaymentItemsToApplicationPayments(pmts);
        let obs$: Observable<WorkflowData> = this.getSaveObservable();

        obs$ = obs$.pipe(           
            tap((app: WorkflowData) => {
                let field = this.application.workflowFieldDatas.find(f => this.paymentFields.includes(f.formControlName));
                appPmts.forEach(pmt => {
                    pmt.applicationNumber = app.applicationNumber;
                    pmt.workflowId = app.id.toString();
                    pmt.workflowFieldId = field.id;
                });
                field.applicationPayments = appPmts;
            }),
            switchMap((app: WorkflowData) => {
                return this.dataService.saveApplication(app);
            }));
        this._loader.toggleLoader(true);
        let subs$ = obs$.subscribe(
            (data: WorkflowData) => {
                this.application = data;
                let pmtField = this.application.workflowFieldDatas.find(f => this.paymentFields.includes(f.formControlName));
                let pmts = pmtField.applicationPayments;
                this._loader.toggleLoader(false);
                this.form.get('payments').get('payments').setValue(pmts);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    onReceiptIssued(fees: ApplicationPayment[]): void {
        let invoiceCtrl = this.form.get('payments').get('invoice');
        let resave: boolean = invoiceCtrl.value == null;
        if (fees != null) {
            for (let f = 0; f < fees.length; f++) {
                let invoiceId = fees[f].queuedInvoice;
                let receiptId = fees[f].queuedReceipt;
                if (invoiceId != null && invoiceId != invoiceCtrl.value) {
                    resave = true;
                    invoiceCtrl.setValue(invoiceId);
                } else if (invoiceId == null && receiptId != null && receiptId != invoiceCtrl.value) {
                    resave = true;
                    invoiceCtrl.setValue(receiptId);
                }
                if (resave) break;
            }
        }
        if (resave) {
            this.form.get('payments').get('payments').setValue(fees);
            let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
                tap((data: WorkflowData) => {
                    this.application = data;
                })
            );
            this._loader.toggleLoader(true);
            let subs$ = obs$.subscribe(
                () => { this._loader.toggleLoader(false); },
                (error) => {
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }
            );
            this.subscription.add(subs$);
        }
    }

    onWellsSelected(evt: Well[]): void {
        //should only ever receive one well, but selector emits a collection so handle as a collection
        let selectedWell = this.form.get('selectedWell');

        let update: boolean = false;
        evt.forEach(w => {
            if (w.id != selectedWell.value) {
                this.form.get('selectedWell').setValue(w.id);
                this.form.get('apiNumber').setValue(w.id);

                this.updateWellForm(w);
                update = true;
            }
        });
        if (update) {
            if (evt.length == 0) {
                this.form.get('selectedWell').setValue(null);
                this.form.get('apiNumber').setValue(null);

                this.updateWellForm(null);
                this.setStep(ApplicationSteps.SelectWell);
            } else {
                this.setStep(ApplicationSteps.Unitization);
            }
        }
    }

    resetRequiredValidation(point: GISPointTypes) {
        let wellForm = this.form.get('wellInformation');
        if (wellForm == null) return;
        let pointsForm = wellForm.get('points');
        if (pointsForm == null) return;
        let ptForm = pointsForm.get(point);
        let validCtrl = ptForm.get('coordinatesValidated');
        if (validCtrl != null) {
            validCtrl.setValue(true);
            validCtrl.updateValueAndValidity();
        }
        let runCtrl = ptForm.get('queryRun');
        if (runCtrl != null) {
            runCtrl.setValue(true);
            runCtrl.updateValueAndValidity();
        }
        this.form.updateValueAndValidity();
    }

    save(prompt: boolean = true): void {
        const obs$ = this.getSaveObservable();
        this._loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this._loader.toggleLoader(false);
                    this.saveUnsubmittedDialog.message = this.saveUnsubmittedMessage.replace('[XXX]', app.applicationNumber);
                    this.saveUnsubmittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.saveUnsubmittedDialog);
                    this.form.markAsPristine();
                    this.allowDeactivate.next(true);
                },
                (err) => {
                    console.error(err);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }
        );
        this.subscription.add(subs$);
    }

    onAccept(): void {
        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed(): void {
        let obs$: Observable<WorkflowData> = this.getSaveObservable();
        this._loader.toggleLoader(true);
        let subs$: Subscription = obs$.pipe(
            switchMap((app: WorkflowData) => {
                this.application = app;
                let req: ApplicationStateChangeRequest = {
                    applicationId: this.application.id,
                    currentState: this.application.applicationState,
                    requestedState: ApplicationState.Approved
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(req);
            })
        ).subscribe(
            (routed: WorkflowData) => {
                this._loader.toggleLoader(false);
                this.acceptConfirmedDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.acceptConfirmedDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    setAuthorizationRecordIds(): void {

    }

    setStep(newStep: ApplicationSteps): void {
        this.form.get('step').setValue(newStep);
    }

    cancel(): void {
        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }

    submit(): void {
        let obs$: Observable<WorkflowData> = this.getSaveObservable();
        this._loader.toggleLoader(true);
        let subs$: Subscription = obs$.pipe(
            switchMap((app: WorkflowData) => {
                this.application = app;
                let req: ApplicationStateChangeRequest = {
                    applicationId:  this.application.id,
                    currentState: this.application.applicationState,
                    requestedState: ApplicationState.InitialReview
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(req);
            })
        ).subscribe(
            (routed: WorkflowData) => {
                this._loader.toggleLoader(false);
                this.submittedDialog.message = this.submittedDialog.message.replace("[APPNUMBER]", routed.applicationNumber);
                this.submittedDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.submittedDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    updateResponse(response: AuthorizationResponse): void {
        let auth: ApplicationCreditAuthorization = <ApplicationCreditAuthorization>response;
        if (auth.CreditCardNumber != null) auth.CreditCardNumber = null;
        let field = this.application.workflowFieldDatas.find(f => f.formControlName == 'payments|creditAuthorization');
        if (field != null) auth.id = field.id;
        if (response.Error == "False") {
            if (this.form != null) {
                let ctrl = this.form.get('payments').get('creditAuthorization');
                if (ctrl != null) {
                    ctrl.setValue(auth);
                }
            }
            let unallocated = parseFloat(response.Amount);
            let feesWithPayments: ApplicationPayment[] = [];
            if (isNaN(unallocated) == false) {
                for (let p = 0; p < this.fees.length; p++) {
                    let fee: ApplicationPayment = this.fees[p];
                    if (fee.amountPaid == null) fee.amountPaid = 0;
                    if (unallocated > 0) {
                        fee.amountDue = fee.fee - fee.amountPaid;
                        if (fee.amountDue <= unallocated) {
                            fee.amountPaid += fee.amountDue;
                            unallocated -= fee.amountDue;
                            let record: Payment = {
                                applicationPaymentId: fee.id,
                                amount: fee.amountDue,
                                instrumentAmount: parseFloat(response.Amount),
                                paymentType: response.PaymentType,
                                paymentDate: new Date(),
                                token: null,
                                tokenId: response.Token,
                                paymentReceivedDate: new Date(),
                                paymentReceivedById: "System",
                                applicationCreditAuthorizations: [
                                    auth
                                ]
                            } as Payment;
                            fee.amountDue -= fee.amountPaid;
                            if (fee.paymentRecords == null) fee.paymentRecords = [];
                            fee.paymentRecords.push(record);
                        } else if (fee.amountDue > unallocated) {
                            fee.amountPaid += unallocated;
                            let record: Payment = {
                                applicationPaymentId: fee.id,
                                amount: unallocated,
                                instrumentAmount: parseFloat(response.Amount),
                                paymentType: response.PaymentType,
                                paymentDate: new Date(),
                                /*       token: response.Token,*/
                                tokenId: response.Token,
                                paymentReceivedDate: new Date(),
                                paymentReceivedById: "System",
                                authorizationId: auth.id
                            } as Payment;
                            fee.amountDue -= unallocated;
                            unallocated = 0;
                            if (fee.paymentRecords == null) fee.paymentRecords = [];
                            fee.paymentRecords.push(record);
                        }

                        feesWithPayments.push(fee);
                    } else {

                        feesWithPayments.push(fee);
                    }
                }
                this.fees = feesWithPayments;

            }
        }
    }

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }

    updateWellForm(stub: Well): void {
        let wellForm = this.form.get("wellInformation");
        if (wellForm != null) {
            if (stub == null) wellForm.reset();
            else {
                wellForm.get('apiNumber').setValue(stub.id);
                let wellName: string = stub.name;
                if (stub.lease != null && stub.lease.name != null && stub.lease.name.length > 0) wellName = stub.lease.name;
                else if (stub.wellNumber != null && stub.wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(stub.wellNumber.trim())) {
                    wellName = wellName.trim().replace(stub.wellNumber.trim(), "");
                }
                wellForm.get("wellName").setValue(wellName);
                wellForm.get("wellNumber").setValue(stub.wellNumber);
                this.form.get("wellName").setValue(wellName);
                this.form.get("wellNumber").setValue(stub.wellNumber);
                if (stub.location != null) {
                    wellForm.get('slant').setValue(stub.location.slant);
                    if (stub.location.gisPoints != null) {
                        stub.location.gisPoints.forEach(pt => {
                            let type = pt.pointType;
                            let subCtrl = wellForm.get('points').get(type);
                            if (subCtrl != null) {
                                if (pt.coordinates != null) {
                                    let latitude: number = pt.coordinates.latitude83 != null ? pt.coordinates.latitude83 : pt.coordinates.latitude;
                                    let longitude: number = pt.coordinates.longitude83 != null ? pt.coordinates.longitude83 : pt.coordinates.longitude;
                                    subCtrl.get('latitude').setValue(latitude);
                                    subCtrl.get('longitude').setValue(longitude);
                                    setTimeout(() => { this.resetRequiredValidation(type); }, 500); //slight delay to try to ensure this runs after invalidate is called
                                }
                                if (pt.county != null) {
                                    subCtrl.get('countyNumber').setValue(pt.county.apiNumber);
                                    subCtrl.get('countyName').setValue(this.casePipe.transform(pt.county.name));
                                } else {
                                    subCtrl.get('countyNumber').setValue(null);
                                    subCtrl.get('countyName').setValue(null);
                                }
                                if (pt.township != null) {
                                    subCtrl.get('townshipName').setValue(this.casePipe.transform(pt.township.name));
                                    subCtrl.get('townshipNumber').setValue(pt.township.number);
                                } else {
                                    subCtrl.get('townshipName').setValue(null);
                                    subCtrl.get('townshipNumber').setValue(null);
                                }
                                if (pt.ogsQuadrangle != null) {
                                    subCtrl.get('quadrangleName').setValue(this.casePipe.transform(pt.ogsQuadrangle.name));
                                    subCtrl.get('quadrangleCode').setValue(pt.ogsQuadrangle.code);
                                } else {
                                    subCtrl.get('quadrangleName').setValue(null);
                                    subCtrl.get('quadrangleCode').setValue(null);
                                }
                                subCtrl.get('section').setValue(pt.section);
                                subCtrl.get('lot').setValue(pt.lot);
                                subCtrl.get('tract').setValue(pt.tract);
                                subCtrl.get('civilTownshipNumber').setValue(pt.civilTownshipNumber);
                            }
                        });
                    }
                   
                }
            }
        }
        let reviewForm = this.form.get('review');
        if (reviewForm != null) {
            reviewForm.get("totalDepthDate").setValue(stub.dateTotalDepthReached);
            if (stub.location != null) reviewForm.get('sourceOfLocation').setValue(stub.location.sourceOfLocation);
        }
    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }



}
